<script setup lang="ts">
import type { GameVehicleSummary } from "~~/types/gamedata";

const props = defineProps<{
  modelValue: string | null;
  vehicles: GameVehicleSummary[];
  /** CSS-Farbe (z.B. `var(--color-aris-400)`) für Akzentrand und Best-Wert-Markierung dieser Spalte. */
  color: string;
  removable: boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string | null];
  remove: [];
}>();

const selected = computed(() => props.vehicles.find((vehicle) => vehicle.id === props.modelValue) ?? null);
</script>

<template>
  <UCard
    variant="outline"
    class="border-t-4 bg-bunker-950 ring-1 ring-white/10 transition-colors"
    :style="{
      borderTopColor: color,
      backgroundColor: selected ? `color-mix(in srgb, ${color} 7%, var(--color-bunker-950))` : undefined,
    }"
    :ui="{ body: 'p-3 sm:p-3' }"
  >
    <div class="flex items-center gap-2">
      <span class="size-2.5 shrink-0 rounded-full" :style="{ backgroundColor: color }" aria-hidden="true" />
      <USelectMenu
        class="min-w-0 flex-1"
        :model-value="modelValue"
        searchable
        :items="vehicles"
        value-key="id"
        label-key="name"
        placeholder="Schiff wählen…"
        size="lg"
        @update:model-value="emit('update:modelValue', $event as string | null)"
      />
      <UButton
        v-if="removable"
        icon="i-lucide-x"
        color="neutral"
        variant="ghost"
        size="sm"
        aria-label="Schiff aus dem Vergleich entfernen"
        @click="emit('remove')"
      />
    </div>

    <div v-if="selected" class="mt-3 flex items-center gap-3">
      <ShipexkursShipImage
        :vehicle="selected"
        img-class="size-12 shrink-0 rounded-md object-cover ring-2"
        :style="{ '--tw-ring-color': color }"
      />
      <div class="min-w-0">
        <p class="!my-0 truncate text-base font-semibold text-white">{{ selected.name }}</p>
        <p class="!my-0 truncate text-xs text-dimmed">{{ selected.manufacturer.name ?? "Unbekannter Hersteller" }}</p>
      </div>
    </div>
  </UCard>
</template>
